import { server } from "../../server";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import * as XLSX from "xlsx";

function EBookList() {
  const [eBook, setEBook] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const todayDate = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState({
    title: "",
    bookNo: "",
    bookType: "",
    language: "",
    class: "",
    subject: "",
    publisher: "",
    author: "",
    description: "",
  });

  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      toast.error("Select File", { position: "top-right" });
      return;
    }

    setUploading(true);

    try {
      const presignedRes = await axios.post(
        "https://7otfgyv0ke.execute-api.ap-south-1.amazonaws.com/prod/online_library_presigned",
        {
          fileName: [selectedFile.name],
        }
      );

      const uploadDetail = presignedRes.data["body-json"].body.uploadDetails[0];
      const presignedUrl = uploadDetail.signedUrl;
      const uploadedFileUrl = uploadDetail.key;

      await axios.put(presignedUrl, selectedFile, {
        headers: {
          "Content-Type": selectedFile.type,
        },
      });

      const payload = {
        title: formData.title,
        bookNo: formData.bookNo,
        bookType: formData.bookType,
        language: formData.language,
        class: formData.class,
        subject: formData.subject,
        publisher: formData.publisher,
        author: formData.author,
        description: formData.description,
        file: uploadedFileUrl,
      };

      await axios.post(
        "https://7otfgyv0ke.execute-api.ap-south-1.amazonaws.com/prod/add_books_online_library_mongodb",
        payload
      );

      toast.success("E-Book Added", { position: "top-right" });
      const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
      if (closeButton) {
        closeButton.click();
      }
      setFormData({
        title: "",
        bookNo: "",
        bookType: "",
        language: "",
        class: "",
        subject: "",
        publisher: "",
        author: "",
        description: "",
      });
      setSelectedFile(null);
    } catch (error) {
      console.error(error);
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const fetchELibraryBookDetails = async () => {
      try {
        const response = await axios.post(
          `${server}/online_library_data_mongodb`,
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
            },
          }
        );
        if (response.data && response.data["body-json"]?.body?.data) {
          setEBook(response.data["body-json"].body.data);
        } else {
          console.error("Invalid data format from API", response.data);
        }
      } catch (error) {
        console.error("Error fetching bus details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchELibraryBookDetails();
  }, []);

  // Filter
  const filteredRecords = eBook.filter((ebook) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(ebook).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower)
        );
      }
      return false;
    });
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleDelete = async (bookIdToDelete) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to delete e-book "${bookIdToDelete}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${server}/delete_online_library_book_mongodb`,
          { bookId: bookIdToDelete },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 200) {
          toast.success("Online Library Book Deleted", {
            position: "top-right",
          });

          setEBook((prev) =>
            prev.filter((book) => book.bookId !== bookIdToDelete)
          );
        } else {
          toast.error("Deletion failed. Please try again.");
        }
      } catch (error) {
        toast.error("Error occurred while deleting. Try again.");
      }
    }
  };

  const handleExportAllEBooksExcel = () => {
    if (!eBook || eBook.length === 0) {
      toast.error("No E-Book data to export");
      return;
    }

    const sheetData = eBook.map((book) => ({
      "E-Book ID": book.bookId || "",
      "E-Book No": book.bookNo || "",
      "E-Book Type": book.bookType || "",
      Title: book.title || "",
      Subject: book.subject || "",
      Class: book.class || "",
      Language: book.language || "",
      Author: book.author || "",
      Publisher: book.publisher || "",
      "E-Book PDF URL": book.file || "",
      "Date Added": book.createdAt
        ? new Date(book.createdAt).toLocaleDateString("en-GB")
        : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "All E-Books");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `All_EBooks_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportEBooksClassWiseExcel = () => {
    if (!eBook || eBook.length === 0) {
      toast.error("No E-Book data to export");
      return;
    }

    const classGroups = {};
    eBook.forEach((book) => {
      const cls = book.class || "Unknown_Class";
      if (!classGroups[cls]) classGroups[cls] = [];
      classGroups[cls].push(book);
    });

    const workbook = XLSX.utils.book_new();

    Object.entries(classGroups).forEach(([className, books]) => {
      const sheetData = books.map((book) => ({
        "E-Book ID": book.bookId || "",
        "E-Book No": book.bookNo || "",
        "E-Book Type": book.bookType || "",
        Title: book.title || "",
        Subject: book.subject || "",
        Class: book.class || "",
        Language: book.language || "",
        Author: book.author || "",
        Publisher: book.publisher || "",
        "E-Book PDF URL": book.file || "",
        "Date Added": book.createdAt
          ? new Date(book.createdAt).toLocaleDateString("en-GB")
          : "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      const sheetName = className.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `EBooks_ClassWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportEBooksBookTypeWiseExcel = () => {
    if (!eBook || eBook.length === 0) {
      toast.error("No E-Book data to export");
      return;
    }

    const bookTypeGroups = {};
    eBook.forEach((book) => {
      const type = book.bookType || "Unknown_Type";
      if (!bookTypeGroups[type]) bookTypeGroups[type] = [];
      bookTypeGroups[type].push(book);
    });

    const workbook = XLSX.utils.book_new();

    Object.entries(bookTypeGroups).forEach(([typeName, books]) => {
      const sheetData = books.map((book) => ({
        "E-Book ID": book.bookId || "",
        "E-Book No": book.bookNo || "",
        "E-Book Type": book.bookType || "",
        Title: book.title || "",
        Subject: book.subject || "",
        Class: book.class || "",
        Language: book.language || "",
        Author: book.author || "",
        Publisher: book.publisher || "",
        "E-Book PDF URL": book.file || "",
        "Date Added": book.createdAt
          ? new Date(book.createdAt).toLocaleDateString("en-GB")
          : "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      const sheetName = typeName.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `EBooks_BookTypeWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportEBooksSubjectWiseExcel = () => {
    if (!eBook || eBook.length === 0) {
      toast.error("No E-Book data to export");
      return;
    }

    const subjectGroups = {};
    eBook.forEach((book) => {
      const subject = book.subject || "Unknown_Subject";
      if (!subjectGroups[subject]) subjectGroups[subject] = [];
      subjectGroups[subject].push(book);
    });

    const workbook = XLSX.utils.book_new();

    Object.entries(subjectGroups).forEach(([subjectName, books]) => {
      const sheetData = books.map((book) => ({
        "E-Book ID": book.bookId || "",
        "E-Book No": book.bookNo || "",
        "E-Book Type": book.bookType || "",
        Title: book.title || "",
        Subject: book.subject || "",
        Class: book.class || "",
        Language: book.language || "",
        Author: book.author || "",
        Publisher: book.publisher || "",
        "E-Book PDF URL": book.file || "",
        "Date Added": book.createdAt
          ? new Date(book.createdAt).toLocaleDateString("en-GB")
          : "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      const sheetName = subjectName.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `EBooks_SubjectWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportEBooksPublisherWiseExcel = () => {
    if (!eBook || eBook.length === 0) {
      toast.error("No E-Book data to export");
      return;
    }

    const publisherGroups = {};
    eBook.forEach((book) => {
      const publisher = book.publisher || "Unknown_Publisher";
      if (!publisherGroups[publisher]) publisherGroups[publisher] = [];
      publisherGroups[publisher].push(book);
    });

    const workbook = XLSX.utils.book_new();

    Object.entries(publisherGroups).forEach(([publisherName, books]) => {
      const sheetData = books.map((book) => ({
        "E-Book ID": book.bookId || "",
        "E-Book No": book.bookNo || "",
        "E-Book Type": book.bookType || "",
        Title: book.title || "",
        Subject: book.subject || "",
        Class: book.class || "",
        Language: book.language || "",
        Author: book.author || "",
        Publisher: book.publisher || "",
        "E-Book PDF URL": book.file || "",
        "Date Added": book.createdAt
          ? new Date(book.createdAt).toLocaleDateString("en-GB")
          : "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      const sheetName = publisherName.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `EBooks_PublisherWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div
            class="modal fade gd-example-modal-lg"
            tabindex="-1"
            role="dialog"
            aria-labelledby="myLargeModalLabel"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header d-flex justify-content-between">
                    <h5
                      className="modal-title fs-4"
                      id="exampleModalToggleLabel"
                    >
                      Add E-Book
                    </h5>
                    <div className="d-flex align-items-center">
                      <span className="badge bg-success-soft me-3 px-3">
                        Editable
                      </span>
                      <div className="form-check form-switch me-4">
                        <input
                          type="checkbox"
                          className="form-check-input m-0"
                          checked
                          readOnly
                        />
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      ></button>
                    </div>
                  </div>

                  <div className="modal-body">
                    <div className="row g-2 mb-2">
                      {/* Title */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Title <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          placeholder="Enter Title"
                          required
                        />
                      </div>

                      {/* Book No */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          E-Book No <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="bookNo"
                          value={formData.bookNo}
                          onChange={handleChange}
                          placeholder="Enter E-Book Number"
                          required
                        />
                      </div>

                      {/* Book Type */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          E-Book Type <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="bookType"
                          value={formData.bookType}
                          onChange={handleChange}
                          placeholder="Enter E-Book Type"
                          required
                        />
                      </div>

                      {/* Language */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Language <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="language"
                          value={formData.language}
                          onChange={handleChange}
                          placeholder="Enter Language"
                          required
                        />
                      </div>

                      {/* Class */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Class <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="class"
                          value={formData.class}
                          onChange={handleChange}
                          placeholder="Enter Class"
                          required
                        />
                      </div>

                      {/* Subject */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Subject <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="Enter Subject"
                          required
                        />
                      </div>

                      {/* Publisher */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Publisher <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="publisher"
                          value={formData.publisher}
                          onChange={handleChange}
                          placeholder="Enter Publisher"
                          required
                        />
                      </div>

                      {/* Author */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Author <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="author"
                          value={formData.author}
                          onChange={handleChange}
                          placeholder="Enter Author"
                          required
                        />
                      </div>

                      {/* Description */}
                      <div className="col-12">
                        <label className="form-label mb-1 ms-3">
                          Description
                        </label>
                        <textarea
                          className="form-control text-dark"
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          placeholder="Enter Description"
                          rows="2"
                          required
                        ></textarea>
                      </div>

                      {/* File Upload */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          File <span className="text-danger">*</span>
                        </label>
                        <input
                          type="file"
                          className="form-control text-dark"
                          onChange={handleFileChange}
                          accept="application/pdf,image/*"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer d-flex justify-content-between py-2">
                    <div>
                      <button
                        type="button"
                        className="btn btn-secondary px-3 me-1 rounded-pill"
                        data-bs-dismiss="modal"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary px-5 rounded-pill fw-normal"
                        disabled={uploading}
                      >
                        {uploading ? (
                          <div
                            class="spinner-border spinner-border-sm"
                            role="status"
                          ></div>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((book, index) => (
                          <tr>
                            <td>1.</td>
                            <td className="lh-sm py-5 text-capitalize">
                              <span class="text-dark">E-Book ID</span> &mdash;{" "}
                              {book.bookId}
                              <br />
                              <span class="text-dark">
                                E-Book No
                              </span> &mdash; {book.bookNo}
                              <br />
                              <span class="text-dark">
                                E-Book Type
                              </span> &mdash; {book.bookType}
                              <br />
                              <span class="text-dark">Title</span> &mdash;{" "}
                              {book.title}
                              <br />
                              <span class="text-dark">
                                E-Book PDF
                              </span> &mdash;{" "}
                              <a href={book.file} target="_blank">
                                View File
                              </a>
                              <br />
                              <span class="text-dark">
                                Subject
                              </span> &mdash; {book.subject} <br />
                              <span class="text-dark">Class</span> &mdash;{" "}
                              {book.class} <br />
                              <span class="text-dark">
                                Language
                              </span> &mdash; {book.language} <br />
                              <span class="text-dark">Author</span> &mdash;{" "}
                              {book.author}
                              <br />
                              <span class="text-dark">
                                Publisher
                              </span> &mdash; {book.publisher}
                              <br />
                              <span class="text-dark">Date</span> &mdash;{" "}
                              {book.createdAt
                                ? new Date(book.createdAt).toLocaleDateString(
                                    "en-GB"
                                  )
                                : ""}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <span class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                  Update
                                </span>
                                <span
                                  onClick={() => handleDelete(book.bookId)}
                                  class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Delete
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="d-flex bg-white justify-content-between rounded-4">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                        <Link
                          data-bs-toggle="modal"
                          data-bs-target=".gd-example-modal-lg"
                          class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                        >
                          Add E-Book
                        </Link>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Library</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              E-Book List
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            E-Book List
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              0.2 MB Size
                            </span>
                          </p>
                        </div>
                        <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleExportAllEBooksExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  All E-Books
                                </span>
                                <span
                                  onClick={handleExportEBooksClassWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Class Wise
                                </span>
                                <span
                                  onClick={handleExportEBooksBookTypeWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Book Type Wise
                                </span>
                                <span
                                  onClick={handleExportEBooksSubjectWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Subject Wise
                                </span>
                                <span
                                  onClick={handleExportEBooksPublisherWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Publisher Wise
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default EBookList;
